/**
 * @file   src/helpers/priceCapUtils.js
 * @brief  Price formatting and demand analysis utilities for Match tab
 */
import {
  BUY,
  LONG_RENT,
} from "../constants/common";

/**
 * Format a price into a compact human-readable string.
 * Examples: 500000 -> "$500K", 1200000 -> "$1.2M", 750 -> "$750"
 *
 * @param {number} value - The price value to format
 * @returns {string} Compact price string with $ prefix
 */
export const formatCompactPrice = (value) => {
  if (value == null || isNaN(value)) return "$0";
  const num = Math.abs(value);
  if (num >= 1000000) {
    const millions = num / 1000000;
    const formatted = millions % 1 === 0
      ? millions.toFixed(0)
      : millions.toFixed(1);
    return `$${formatted}M`;
  }
  if (num >= 1000) {
    const thousands = num / 1000;
    const formatted = thousands % 1 === 0
      ? thousands.toFixed(0)
      : thousands.toFixed(1);
    return `$${formatted}K`;
  }
  return `$${Math.round(num)}`;
};

/**
 * Find the bin index with the highest count (peak demand).
 *
 * @param {number[]} prices - Array of numeric prices
 * @param {number} numBins - Number of bins to divide into
 * @param {number} max - Maximum price value for bin range
 * @returns {number|null} Index of the peak bin, or null if insufficient data
 */
export const computePeakDemandBin = (prices, numBins, max) => {
  if (!prices || prices.length < 3 || !max || max <= 0 || !numBins) return null;

  const binWidth = max / numBins;
  const bins = new Array(numBins).fill(0);

  prices.forEach((price) => {
    let idx = Math.floor(price / binWidth);
    if (idx >= numBins) idx = numBins - 1;
    if (idx < 0) idx = 0;
    bins[idx]++;
  });

  let peakIdx = 0;
  for (let i = 1; i < numBins; i++) {
    if (bins[i] > bins[peakIdx]) peakIdx = i;
  }

  return bins[peakIdx] > 0 ? peakIdx : null;
};

export const DEMAND_BIN_COUNT = 12;
const SWEET_SPOT_WINDOW = 3;

const sweetSpotRounding = (price, adType) => {
  if (adType === BUY) return price < 500000 ? 25000 : 50000;
  if (adType === LONG_RENT) return 100;
  return 50;
};

export const computeDemandBins = (
  prices,
  rangeMin,
  rangeMax,
  numBins = DEMAND_BIN_COUNT,
) => {
  const low = Number(rangeMin) || 0;
  const high = Number(rangeMax);
  if (!prices || !Number.isFinite(high) || high <= low || !numBins) return null;

  const binWidth = (high - low) / numBins;
  const bins = Array.from({ length: numBins }, (_, i) => ({
    count: 0,
    start: low + i * binWidth,
    end: low + (i + 1) * binWidth,
  }));
  let below = 0;
  let above = 0;

  prices.forEach((price) => {
    if (price < low) {
      below++;
      return;
    }
    if (price > high) {
      above++;
      return;
    }
    bins[Math.min(Math.floor((price - low) / binWidth), numBins - 1)].count++;
  });

  const inRange = bins.reduce((sum, bin) => sum + bin.count, 0);
  return { bins, below, above, inRange };
};

export const findPeakBinIndex = (bins) => {
  if (!bins || bins.length === 0) return null;
  let peakIdx = 0;
  bins.forEach((bin, i) => {
    if (bin.count > bins[peakIdx].count) peakIdx = i;
  });
  return bins[peakIdx].count > 0 ? peakIdx : null;
};

export const findBinIndexForPrice = (bins, price) => {
  if (!bins || bins.length === 0 || price == null) return null;
  const last = bins.length - 1;
  if (price < bins[0].start || price > bins[last].end) return null;
  const idx = bins.findIndex((bin) => price >= bin.start && price < bin.end);
  return idx === -1 ? last : idx;
};

export const computeSweetSpotFromBins = (bins) => {
  if (!bins || bins.length === 0) return null;
  const width = Math.min(SWEET_SPOT_WINDOW, bins.length);
  let bestStart = -1;
  let bestCount = 0;
  for (let i = 0; i + width <= bins.length; i++) {
    const count = bins
      .slice(i, i + width)
      .reduce((sum, bin) => sum + bin.count, 0);
    if (count > bestCount) {
      bestCount = count;
      bestStart = i;
    }
  }
  if (bestStart === -1) return null;

  let first = bestStart;
  let last = bestStart + width - 1;
  while (bins[first].count === 0) first++;
  while (bins[last].count === 0) last--;

  return {
    min: bins[first].start,
    max: bins[last].end,
    count: bestCount,
    firstBin: first,
    lastBin: last,
  };
};

const labelStep = (price) => {
  if (price >= 1000000) return 25000;
  if (price >= 250000) return 5000;
  if (price >= 50000) return 1000;
  if (price >= 5000) return 100;
  return 10;
};

export const sweetSpotLabelRange = (spot) => {
  if (!spot) return null;
  const step = labelStep(spot.max);
  const min = Math.floor(spot.min / step) * step;
  const max = Math.max(Math.ceil(spot.max / step) * step, min + step);
  return { min, max };
};

// Same rule as the sweet spot: no peak claim when more buyers sit outside the
// chart than in the tallest bar.
export const computePeakBinIndex = (demand) => {
  if (!demand) return null;
  const idx = findPeakBinIndex(demand.bins);
  if (idx == null || demand.bins[idx].count < Math.max(demand.above, demand.below)) {
    return null;
  }
  return idx;
};

export const computeSweetSpot = (demand) => {
  if (!demand) return null;
  const spot = computeSweetSpotFromBins(demand.bins);
  if (!spot || spot.count < Math.max(demand.above, demand.below)) return null;
  return spot;
};

const rangeFromPrices = (prices, adType) => {
  const lowest = Math.min(...prices);
  const highest = Math.max(...prices);
  const roundTo = sweetSpotRounding(lowest, adType);
  const min = Math.floor(lowest / roundTo) * roundTo;
  const max = Math.max(Math.ceil(highest / roundTo) * roundTo, min + roundTo);
  return { min, max };
};

export const computeSweetSpotRange = (prices, adType, rangeMin, rangeMax) => {
  if (!prices || prices.length < 3) return null;

  const range =
    Number(rangeMax) > (Number(rangeMin) || 0)
      ? { min: Number(rangeMin) || 0, max: Number(rangeMax) }
      : rangeFromPrices(prices, adType);

  const demand = computeDemandBins(prices, range.min, range.max);
  return computeSweetSpot(demand);
};

/**
 * Calculate slider range centered on a property's asking price with tiered padding.
 * Used on the Match tab when a property is selected.
 *
 * @param {number} price - The property's asking price
 * @param {string} adType - BUY, LONG_RENT, or SHORT_RENT
 * @returns {{ min: number, max: number, step: number }|null}
 */
export const calculatePropertyPriceRange = (price, adType) => {
  if (!price || price <= 0) return null;

  let padding, step;

  if (adType === BUY) {
    if (price < 300000) {
      padding = 50000;
      step = 5000;
    } else if (price < 1000000) {
      padding = 100000;
      step = 10000;
    } else if (price < 3000000) {
      padding = 500000;
      step = 25000;
    } else {
      padding = 1000000;
      step = 50000;
    }
  } else if (adType === LONG_RENT) {
    if (price < 2000) {
      padding = 500;
      step = 50;
    } else if (price < 5000) {
      padding = 1000;
      step = 100;
    } else {
      padding = 2000;
      step = 200;
    }
  } else {
    // STR (nightly)
    if (price < 200) {
      padding = 50;
      step = 10;
    } else if (price < 500) {
      padding = 100;
      step = 25;
    } else {
      padding = 200;
      step = 50;
    }
  }

  const rangeMin = Math.max(0, Math.floor((price - padding) / step) * step);
  const rangeMax = Math.ceil((price + padding) / step) * step;

  return { min: rangeMin, max: rangeMax, step };
};
